import { registerComponent, asyncRequest } from "helpers";
import { WYATTElement } from "wyatt";

const ADOPTION_SEARCH_URL = "/pages/adoption-search";

registerComponent(
  "adoption-search-textbooks",
  class AdoptionSearchTextbooks extends WYATTElement {
    constructor() {
      super();

      this.backButtonElement = this.querySelector("[data-back-button]");

      this.printButtonInstance = this.querySelector(
        "adoption-search-print-button"
      );

      this.scholarshipEligibilityToggleInstance = this.querySelector(
        "adoption-search-scholarship-eligibility-toggle"
      );

      this.courseListInstance = this.querySelector(
        "adoption-search-course-list"
      );

      this.textbookResultsInstance = this.querySelector(
        "adoption-search-textbook-results"
      );

      this.textbookData = null;
      this.bookSearchDataByCourseId = null;
      this.isbnByCourseId = null;
      this.bookDataByCourseId = null;
      this.productsByIsbn = null;
      this.searchResultsByCourseId = null;
      this.productTemplatesByCourseId = null;
    }

    async connectedCallback() {
      this.validateAccess();
      this.updateBackButtonHref();

      await this.buildData();

      this.renderUI();
    }

    // GETTERS

    get currentParams() {
      const currentPage = new URL(window.location.href);
      return new URLSearchParams(currentPage.search);
    }

    get isReferrerValid() {
      if (this.currentParams.get("courseId[]") === null) return false;

      return true;
    }

    get hasResults() {
      return Object.keys(this.productTemplatesByCourseId ?? {}).length > 0;
    }

    get sortedCourseIds() {
      this.sortedCourseIdMemo =
        this.sortedCourseIdMemo ??
        Object.keys(this.productTemplatesByCourseId).sort();

      return this.sortedCourseIdMemo;
    }

    // API HELPERS

    async fetchTextbookData() {
      const courseIds = this.currentParams.getAll("courseId[]").join(",");
      const endpoint = `${this.config.endpoints.books}?courseIds=${courseIds}`;

      return asyncRequest(endpoint, {}, { responseFormat: "json" });
    }

    async fetchProductSearchSuggestions(searchTerm) {
      const queryString = new URLSearchParams({
        q: searchTerm,
        "resources[type]": "product",
        "resources[options][fields]": "variants.barcode,variants.sku,title,body",
      }).toString();

      const endpoint = `${window.Shopify.routes.root}search/suggest.json?${queryString}`;

      const response = await asyncRequest(
        endpoint,
        {},
        { responseFormat: "json" }
      );

      return response?.resources?.results?.products ?? [];
    }

    async fetchProductSearchSuggestionsByBook(book) {
      const searchTerms = [book.barcode, book.isbn].filter(Boolean);

      for (const searchTerm of searchTerms) {
        const products = await this.fetchProductSearchSuggestions(searchTerm);
        if (products.length > 0) {
          return products;
        }
      }

      return [];
    }

    async fetchProductTemplate(product, matReqId = null) {
      const view =
        matReqId === "1" ? "textbook-summary-required" : "textbook-summary";
      const endpoint = `${window.Shopify.routes.root}products/${product.handle}?view=${view}`;

      const response = await asyncRequest(endpoint);

      return response;
    }

    // VALIDATION

    validateAccess() {
      if (this.isReferrerValid) return;
      window.location.href = ADOPTION_SEARCH_URL;
    }

    updateBackButtonHref() {
      this.backButtonElement.href = document.referrer;
    }

    // DATA BUILDERS

    async buildData() {
      this.textbookData = await this.fetchTextbookData();

      this.bookSearchDataByCourseId = this.buildBookSearchDataByCourseId();
      this.isbnByCourseId = this.buildIsbnByCourseId();
      this.bookDataByCourseId = this.buildBookDataByCourseId();
      this.searchResultsByCourseId = await this.buildSearchResultsByCourseId();

      this.productTemplatesByCourseId =
        await this.buildProductTemplatesByCourseId();
    }

    buildIsbnByCourseId() {
      return this.textbookData.items.reduce((acc, item) => {
        acc[item.course_id] = item.books.map((book) => book.isbn);
        return acc;
      }, {});
    }

    buildBookSearchDataByCourseId() {
      return this.textbookData.items.reduce((acc, item) => {
        acc[item.course_id] = item.books.map((book) => ({
          isbn: book.isbn,
          barcode: book.barcode,
        }));
        return acc;
      }, {});
    }

    buildBookDataByCourseId() {
      return this.textbookData.items.reduce((acc, item) => {
        acc[item.course_id] = item.books.map((book) => ({
          isbn: book.isbn,
          matReqId: book.mat_req_id,
          matReqName: book.mat_req_name,
        }));
        return acc;
      }, {});
    }

    async buildSearchResultsByCourseId() {
      const productsByIsbn = {};

      const result = await Object.entries(this.bookSearchDataByCourseId).reduce(
        async (accPromise, [courseId, books]) => {
          const acc = await accPromise;

          const productArrays = await Promise.all(
            books.map(async (book) => {
              const products = await this.fetchProductSearchSuggestionsByBook(
                book
              );

              products.forEach((product) => {
                productsByIsbn[product.id] = book.isbn;
              });

              return products;
            })
          );

          const products = productArrays.flat();

          acc[courseId] = products;

          return acc;
        },
        Promise.resolve({})
      );

      this.productsByIsbn = productsByIsbn;

      return result;
    }

    async buildProductTemplatesByCourseId() {
      return Object.entries(this.searchResultsByCourseId).reduce(
        async (accPromise, [courseId, products]) => {
          const acc = await accPromise;
          const bookData = this.bookDataByCourseId[courseId] || [];

          const templateArrays = await Promise.all(
            products.map((product) => {
              // Get the ISBN that was used to find this product
              const productIsbn = this.productsByIsbn[product.id];

              // Find the matching book data by ISBN
              const matchingBook = bookData.find(
                (book) => book.isbn === productIsbn
              );

              const matReqId = matchingBook ? matchingBook.matReqId : null;

              return this.fetchProductTemplate(product, matReqId);
            })
          );

          const templates = templateArrays.flat();

          acc[courseId] = templates;

          return acc;
        },
        Promise.resolve({})
      );
    }

    // UI

    renderUI() {
      if (this.hasResults) {
        this.printButtonInstance.render();
        this.scholarshipEligibilityToggleInstance.render();
      }

      this.courseListInstance.render(this.textbookData, this.sortedCourseIds);

      this.textbookResultsInstance.render(
        this.textbookData,
        this.productTemplatesByCourseId,
        this.sortedCourseIds
      );
    }
  }
);